import { Mediaclip } from './../interfaces/mediaclip';
import { FilterConfig } from './filter-set.service';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, ReplaySubject } from 'rxjs';
import { Publication, CustomField } from './../interfaces/publication';
import { CustomFieldsService } from './custom-fields.service';
import { PublicationService } from './publication.service';

export interface MediaclipRelations {
	[entityType: string]: string[];
}

@Injectable()
export class MediaclipService {
	publication: Publication;
	subscriptions: Subscription = new Subscription();
	mediaclipCustomFields: ReplaySubject<CustomField[]> = new ReplaySubject<CustomField[]>(1);

	constructor(
		private translate: TranslateService,
		private publicationService: PublicationService,
		private customFieldsService: CustomFieldsService
	) {
		this.publication = {} as Publication;
		this.subscriptions.add(this.publicationService.getData().subscribe((publicationData: Publication) => {
			this.publication = publicationData;
			let customFields: CustomField[] = [];
			if (this.publication && this.publication.customfields) {
				if (this.publication.customfields.hasOwnProperty('mediaclip')) {
					customFields = this.publication.customfields.mediaclip;
				}
			}
			this.mediaclipCustomFields.next(customFields);
		}));
	}

	getRelations(clip: Mediaclip): MediaclipRelations {
		const relationRef = {};
		if (clip?.relationLabel?.length) {
			clip.relationLabel.forEach((label) => {
				const entityTypePartRegex = new RegExp(/[a-zA-Z]+/g);
				const entityType = label.match(entityTypePartRegex) && label.match(entityTypePartRegex)[0];
				const id = label.split(entityType)[1];
				if (entityType && id) {
					const ref = relationRef[entityType.toLowerCase()] = relationRef[entityType.toLowerCase()] || [];
					ref.push(id);
				}
			});
		}
		return relationRef;
	}

	getMediaSelectorFilterConfig(): FilterConfig[] {
		return [
			{
				property: 'title',
				label: this.translate.instant('TITLE'),
				type: 'text'
			}, {
				property: 'createdBy',
				label: this.translate.instant('CREATED_BY'),
				type: 'multiSelect'
			}, {
				property: 'createddate',
				label: this.translate.instant('CREATED_DATE'),
				type: 'date'
			}, {
				property: 'updateddate',
				label: this.translate.instant('UPDATED_DATE'),
				type: 'date'
			},  {
				property: 'updatedBy',
				label: this.translate.instant('UPDATED_BY'),
				type: 'multiSelect'
			}, {
				property: 'publisheddate',
				label: this.translate.instant('PUBLISHED_DATE'),
				type: 'date'
			}, {
				property: 'fulltext',
				label: this.translate.instant('FULL_TEXT'),
				type: 'text',
			}, {
				property: 'author',
				label: this.translate.instant('AUTHOR'),
				type: 'text'
			}, {
				property: 'cat',
				label: this.translate.instant('TAGS'),
				type: 'multiSelect'
			}, {
				property: 'status',
				label: this.translate.instant('STATUS'),
				type: 'select'
			}, {
				property: 'sourceid',
				label: this.translate.instant('SOURCE_ID'),
				type: 'text'
			}
		];
	}

	getFilterConfigOptions(): FilterConfig[] {
		return [
			{
				property: 'mediatype',
				label: this.translate.instant('MEDIATYPE'),
				type: 'multiSelect'
			}, {
				property: 'sourcetype',
				label: this.translate.instant('SOURCETYPE'),
				type: 'select'
			}, {
				property: 'title',
				label: this.translate.instant('TITLE'),
				type: 'text'
			}, {
				property: 'length_int', // can this be "length" ?
				label: this.translate.instant('DURATION'),
				type: 'facetquery',
				options: [
					{label: '00 - 15s', query: '[0 TO 15]'},
					{label: '15 - 30s', query: '[15 TO 30]'},
					{label: '30 - 45s', query: '[30 TO 45]'},
					{label: '45 - 60s', query: '[45 TO 60]'},
					{label: '01 - 02m', query: '[60 TO 120]'},
					{label: '02 - 03m', query: '[120 TO 180]'},
					{label: '03 - 05m', query: '[180 TO 300]'},
					{label: '05 - 10m', query: '[300 TO 600]'},
					{label: '10 - 15m', query: '[600 TO 900]'},
					{label: '15 - 30m', query: '[900 TO 1800]'},
					{label: '30 - 45m', query: '[1800 TO 2700]'},
					{label: '45 - 60m', query: '[2700 TO 3600]'},
					{label: '01 - 02h', query: '[3600 TO 7200]'},
					{label: '02h +', query: '[7200 TO *]'}
				]
			}, {
				property: 'deeplink',
				label: this.translate.instant('DEEP_LINK'),
				type: 'text'
			}, {
				property: 'originalfilename',
				label: this.translate.instant('ORIGINAL_FILENAME'),
				type: 'text',
			}, {
				property: 'fulltext',
				label: this.translate.instant('FULL_TEXT'),
				type: 'text',
			}, {
				property: 'author',
				label: this.translate.instant('AUTHOR'),
				type: 'text'
			}, {
				property: 'cat',
				label: this.translate.instant('TAGS'),
				type: 'multiSelect'
			}, {
				property: 'status',
				label: this.translate.instant('STATUS'),
				type: 'select'
			}, {
				property: 'createdBy',
				label: this.translate.instant('CREATED_BY'),
				type: 'multiSelect'
			}, {
				property: 'updatedBy',
				label: this.translate.instant('UPDATED_BY'),
				type: 'multiSelect'
			}, {
				property: 'publisheddate',
				label: this.translate.instant('PUBLISHED_DATE'),
				type: 'date'
			}, {
				property: 'createddate',
				label: this.translate.instant('CREATED_DATE'),
				type: 'date'
			}, {
				property: 'updateddate',
				label: this.translate.instant('UPDATED_DATE'),
				type: 'date'
			}, {
				property: 'sourceid',
				label: this.translate.instant('SOURCE_ID'),
				type: 'text'
			}, {
				property: 'isEmpty',
				label: this.translate.instant('IS_EMPTY'),
				type: 'boolean'
			}, {
				property: 'isThreeSixtyVideo',
				label: this.translate.instant('IS_THREE_SIXTY_VIDEO'),
				type: 'boolean'
			}, {
				property: 'disablecommercials',
				label: this.translate.instant('DISABLE_COMMERCIALS'),
				type: 'boolean'
			}, {
				property: 'hasInteractivity',
				label: this.translate.instant('HAS_INTERACTIVITY'),
				type: 'boolean'
			}
		];
	}

	getPropertyOptions() {
		const mcFields = [
			{
				label: this.translate.instant('ID'),
				type: 'string',
				value: 'id',
				readOnly: true,
				description: ''
			},
			{
				label: this.translate.instant('TITLE'),
				type: 'string',
				value: 'title',
				description: ''
			},
			{
				label: this.translate.instant('DURATION'),
				type: 'int',
				value: 'length',
				description: 'FIELD_DESCRIPTION_LENGTH'
			},
			{
				label: this.translate.instant('VIEWS'),
				type: 'int',
				value: 'views',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_VIEWS'
			},
			{
				label: this.translate.instant('TAG'),
				type: 'multistring',
				value: 'cat',
				description: ''
			},
			{
				label: this.translate.instant('TRANSCODING_FINISHED'),
				type: 'string',
				value: 'transcodingFinished',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_TRANSCODING_FINISHED'
			},
			{
				label: this.translate.instant('USER_ID'),
				type: 'string',
				value: 'userid',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_USER_ID'
			},
			{
				label: this.translate.instant('DEEP_LINK'),
				type: 'string',
				value: 'deeplink',
				description: 'FIELD_DESCRIPTION_DEEP_LINK'
			},
			{
				label: this.translate.instant('GENERATED_DEEP_LINK'),
				type: 'string',
				value: 'gendeeplink',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_GENERATED_DEEP_LINK'
			},
			{
				label: this.translate.instant('DESCRIPTION'),
				type: 'string',
				value: 'description',
				description: ''
			},
			{
				label: this.translate.instant('STATUS'),
				type: 'string',
				value: 'status',
				description: 'FIELD_DESCRIPTION_STATUS'
			},
			{
				label: this.translate.instant('COPYRIGHT'),
				type: 'string',
				value: 'copyright',
				description: ''
			},
			{
				label: this.translate.instant('MEDIATYPE'),
				type: 'string',
				value: 'mediatype',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_MEDIATYPE'
			},
			{
				label: this.translate.instant('SOURCETYPE'),
				type: 'string',
				value: 'sourcetype',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_SOURCETYPE'
			},
			{
				label: this.translate.instant('AUTHOR'),
				type: 'string',
				value: 'author',
				description: ''
			},
			{
				label: this.translate.instant('ORIGINAL_FILENAME'),
				type: 'string',
				value: 'originalfilename',
				readOnly: true,
				description: 'FIELD_DESCRIPTION_ORIGINAL_FILENAME'
			},
			{
				label: this.translate.instant('MODIFIED_DATE'),
				type: 'string',
				value: 'modifieddate',
				readOnly: true,
				description: ''
			},
			{
				label: this.translate.instant('CREATED_DATE'),
				type: 'string',
				value: 'createddate',
				readOnly: true,
				description: ''
			},
			{
				label: this.translate.instant('SOURCE_ID'),
				type: 'string',
				value: 'sourceid',
				description: 'FIELD_DESCRIPTION_SOURCE_ID'
			}
		];
		const customFields = this.customFieldsService.getCustomFields('mediaclip').map(field => ({...field, ...{value: field.name}}));

		const mediaclipOptions: any[] = [...mcFields, ...customFields];
		return  mediaclipOptions;
	}
}
